import { useCallback, useEffect, useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import Link from '@mui/material/Link'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { ExerciseRenderer } from '@/components/exercises/renderers/ExerciseRenderer'
import { readableStudentAnswerSx } from '@/components/exercises/readableStudentAnswerSx'
import type { ExerciseRun, ExerciseType } from '@/api/exercises'
import {
  languageProfileApi,
  PROFILE_LEVELS,
  type PlacementRunDetail,
  type PlacementRunItemDetail,
  type ProfileLevel,
  type StudentLanguageProfile,
} from '@/api/placement'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { ROUTES } from '@/utils/constants'
import { formatDateDMY } from '@/utils/date'
import '@/styles/dashboard.css'

/**
 * Разбор одного прогона теста уровня — учителю.
 *
 * Здесь и только здесь он видит пробы письма и речи: автопроверки у них нет, в
 * общую очередь проверки они не идут, и прочитать их больше негде. Уровень по
 * итогам ставится корректировкой профиля на странице языкового профиля.
 *
 * Правильных ответов на странице нет и у учителя. Банк один на всех, пересдача
 * идёт по тем же заданиям, и учитель, знающий ключи, может — пусть из лучших
 * побуждений — подсказать и обнулить измерение. Что решено верно, видно
 * пометкой у задания.
 */
export function PlacementRunDetailPage() {
  const { t } = useTranslation()
  const { id, runId } = useParams<{ id: string; runId: string }>()
  const userId = Number(id)
  const placementRunId = Number(runId)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [run, setRun] = useState<PlacementRunDetail | null>(null)
  // Профиль нужен ради уровней письма и речи: их ставит человек, и здесь же
  // видно, что уже стоит.
  const [profile, setProfile] = useState<StudentLanguageProfile | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const [detail, profileRow] = await Promise.all([
        languageProfileApi.run(userId, placementRunId),
        languageProfileApi.show(userId).catch(() => null),
      ])

      setRun(detail)
      setProfile(profileRow)
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setLoading(false)
    }
  }, [placementRunId, t, userId])

  useEffect(() => {
    if (!Number.isFinite(userId) || !Number.isFinite(placementRunId)) {
      setError(t('common.error'))
      setLoading(false)

      return
    }

    void load()
  }, [load, placementRunId, t, userId])

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Link component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('dashboard.students')}
          </Link>
          <Link component={RouterLink} to={`/users/${userId}`} underline="hover">
            {run?.student.name ?? t('profile.title')}
          </Link>
          <Link component={RouterLink} to={ROUTES.USER_LANGUAGE_PROFILE(userId)} underline="hover">
            {t('placement.runsTitle')}
          </Link>
          <Typography color="text.primary">
            {run !== null ? formatDateDMY(run.completed_at ?? run.started_at) : t('placement.runTitle')}
          </Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h5">{t('placement.runTitle')}</Typography>
          <PageHelpPopover pageKey="placement-run" />
        </Box>

        {error !== null && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rectangular" height={320} />
        ) : run === null ? null : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Chip
                    size="small"
                    color={run.status === 'completed' ? 'success' : 'default'}
                    variant={run.status === 'completed' ? 'filled' : 'outlined'}
                    label={t(`placement.runStatus.${run.status}`)}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatDateDMY(run.completed_at ?? run.started_at)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('placement.runBlocks', { count: run.blocks })}
                  </Typography>
                  {run.self_assessed_level !== null && (
                    <Typography variant="body2" color="text.secondary">
                      {t('placement.selfAssessedIs', { level: run.self_assessed_level })}
                    </Typography>
                  )}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Object.entries(run.skill_scores)
                    .filter(([, score]) => score.total > 0)
                    .map(([skill, score]) => (
                      <Chip
                        key={skill}
                        size="small"
                        variant="outlined"
                        label={`${t(`placement.skills.${skill}`)}: ${t('placement.scoreOf', {
                          correct: score.correct,
                          total: score.total,
                        })}`}
                      />
                    ))}
                </Stack>
              </CardContent>
            </Card>

            {/* Предупреждение до заданий, а не после: прочитанное внизу
                приходит, когда экран уже показан ученику через плечо. */}
            <Alert severity="warning" variant="outlined" sx={{ mb: 3, borderWidth: 2 }}>
              <AlertTitle>{t('placement.runKeepPrivateTitle')}</AlertTitle>
              {t('placement.runKeepPrivate')}
            </Alert>

            {run.items.length === 0 ? (
              <Alert severity="info">{t('placement.runNoItems')}</Alert>
            ) : (
              <Stack spacing={2}>
                {run.items.map((item, index) => (
                  <RunItem
                    key={item.id}
                    item={item}
                    number={index + 1}
                    userId={userId}
                    grade={(profile?.skill_levels?.[item.skill] as ProfileLevel | undefined) ?? null}
                    onGraded={setProfile}
                  />
                ))}
              </Stack>
            )}

            <Alert severity="warning" variant="outlined" sx={{ mt: 3, borderWidth: 2 }}>
              <AlertTitle>{t('placement.runKeepPrivateTitle')}</AlertTitle>
              {t('placement.runKeepPrivate')}
            </Alert>
          </>
        )}
      </Box>
    </DashboardLayout>
  )
}

/** Одно задание: как его видел ученик, с его ответами и разбором. */
function RunItem({
  item,
  number,
  userId,
  grade,
  onGraded,
}: {
  item: PlacementRunItemDetail
  number: number
  userId: number
  /** Уровень навыка в профиле: пробе он и есть выставленная оценка. */
  grade: ProfileLevel | null
  onGraded: (profile: StudentLanguageProfile) => void
}) {
  const { t } = useTranslation()

  const audio = typeof item.answers?.audio_url === 'string' ? item.answers.audio_url : null
  const isProbe = item.evaluation_criteria !== null
  // «Сдал пустым» и «ошибся» — разные новости. Блок сдаётся целиком, поэтому
  // сервер помечает отвеченными и нетронутые задания: пустой ответ и есть
  // единственный признак того, что человек его не делал.
  const skipped = isEmptyAnswer(item.answers)

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1.5 }}>
          {/* Номер вместо заголовка задания: заголовок иногда выдаёт, о чём оно. */}
          <Typography variant="subtitle2">{t('placement.itemNumber', { number })}</Typography>
          <Chip size="small" variant="outlined" label={t(`placement.skills.${item.skill}`)} />
          <Chip size="small" variant="outlined" label={item.level} />
          {/* У проб исхода нет: балл им ставит человек, и «неверно» здесь
              означало бы автоматический вердикт, которого не было. */}
          {skipped ? (
            <Chip size="small" variant="outlined" label={t('placement.itemSkipped')} />
          ) : item.is_correct === null ? (
            <Chip
              size="small"
              variant="outlined"
              color={isProbe ? 'warning' : 'default'}
              label={isProbe ? t('placement.awaitingTeacher') : t('placement.itemNotAnswered')}
            />
          ) : (
            <Chip
              size="small"
              color={item.is_correct ? 'success' : 'error'}
              label={item.is_correct ? t('placement.itemCorrect') : t('placement.itemWrong')}
            />
          )}
        </Stack>

        {item.task.instructions !== null && item.task.instructions !== '' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, whiteSpace: 'pre-line' }}>
            {item.task.instructions}
          </Typography>
        )}

        {/* Задание рисует тот же рендерер, что и в решении: учитель видит
            работу глазами ученика — поля заполнены его ответами, верное
            зелёным, неверное красным. Своя вёрстка на восемнадцать типов
            разошлась бы с настоящей на первом же изменении. */}
        {skipped ? (
          <Typography variant="body2" color="text.disabled">
            {isProbe ? t('placement.probeNotAnswered') : t('placement.itemSkippedHint')}
          </Typography>
        ) : (
          <Box sx={readableStudentAnswerSx}>
            <ExerciseRenderer
              type={item.task.type as ExerciseType}
              content={item.task.content}
              answers={(item.answers ?? {}) as Record<string, unknown>}
              onChange={() => undefined}
              readOnly
              run={{ check_details: item.check_details } as unknown as ExerciseRun}
            />
          </Box>
        )}

        {audio !== null && <Box component="audio" controls src={audio} sx={{ width: '100%', mt: 1 }} />}

        {item.evaluation_criteria !== null && !skipped && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {t('placement.probeCriteria')}
            </Typography>
            <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
              {Object.entries(item.evaluation_criteria).map(([key, value]) => (
                <Typography key={key} component="li" variant="body2">
                  {value}
                </Typography>
              ))}
            </Stack>

            {/* Оценка ставится здесь же: пробу читают ради неё, и уводить
                учителя за уровнем в другой экран значит терять из виду то,
                что он только что прочитал. */}
            <ProbeGrade userId={userId} skill={item.skill} current={grade} onGraded={onGraded} />
          </>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * Ответ пуст: ученик задание не трогал.
 *
 * Пустой считается и отсутствующая карта, и карта, все значения которой пусты
 * — блок сдаётся целиком, и нетронутое задание приезжает как `{}` или `[]`.
 */
function isEmptyAnswer(answers: Record<string, unknown> | null): boolean {
  if (answers === null) return true

  const values = Object.values(answers)

  if (values.length === 0) return true

  return values.every((value) => isEmptyValue(value))
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.every((row) => isEmptyValue(row))
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).every(isEmptyValue)

  return false
}

/**
 * Оценка пробы письма или речи.
 *
 * Автопроверки у них нет по природе: у сочинения и устного ответа нет
 * правильного варианта, с которым можно сверить. Уровень ставит человек, и
 * ставится он тем же способом, что и любая правка уровня — то есть перебивает
 * расчёт теста и остаётся в профиле.
 */
function ProbeGrade({
  userId,
  skill,
  current,
  onGraded,
}: {
  userId: number
  skill: string
  current: ProfileLevel | null
  onGraded: (profile: StudentLanguageProfile) => void
}) {
  const { t } = useTranslation()

  const [value, setValue] = useState<ProfileLevel | ''>(current ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    if (value === '') return

    setBusy(true)
    setError(null)

    try {
      onGraded(
        await languageProfileApi.correctLevels(userId, {
          skill_levels: { [skill]: value },
        }),
      )
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ mt: 2 }}>
      {error !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label={t('placement.probeGradeLabel')}
          value={value}
          disabled={busy}
          onChange={(event) => setValue(event.target.value as ProfileLevel | '')}
          sx={{ minWidth: 200 }}
        >
          {PROFILE_LEVELS.map((level) => (
            <MenuItem key={level} value={level}>
              {level}
            </MenuItem>
          ))}
        </TextField>

        <Button variant="contained" size="small" disabled={busy || value === ''} onClick={() => void save()}>
          {busy ? t('common.loading') : t('placement.probeGradeSave')}
        </Button>

        {current !== null && (
          <Typography variant="caption" color="text.secondary">
            {t('placement.probeGradeCurrent', { level: current })}
          </Typography>
        )}
      </Stack>
    </Box>
  )
}
